import { Box, Button, Flex, Grid, IconButton } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { createWebSocketEventListener } from "utils/web-socket-events";
import { AttendanceChatProps } from "../chats-context/types";
import { useChatsContext } from "../chats-context";
import { sortArrayBy } from "utils/filter-array-by";
import { SkeletonLoading } from "components/skeleton-loading";
import { calcTelaChatsHeight } from "../..";
import { useAttendanceContext } from "contexts/attendance-context";
import ConversationAttendanceCard from "../conversation-attendance-card";
import {
  AttendanceFilterChats,
  chatsFilterButtonsFontSize,
} from "./attendance-filter-chats";
import { Convenio } from "components/atendimentos-components/atendimento-form/types";
import { TabulacaoType } from "components/atendimentos-components/kanban-and-table-atendimento/kanban/parts/types";
import { CleanIcon } from "components/vectors/clean-icon";
import { checkIsValidAttendance, getUsers } from "./functions";
import { DropdownField } from "components/dropdown-field";
import { useApplicationContext } from "contexts/ApplicationContext";
import { DropdownOptionProps } from "components/dropdown";
import apiCarteira from "api/api-carteira";
import { DeleteIcon } from "@chakra-ui/icons";
import { IconButtonWithLoading } from "components/mailing-components/mailing-table/icon-button-with-loading";

export interface AttendanceChatFilterProps {
  convenio?: Convenio;
  tabulacoes?: TabulacaoType;
  fromDataCriacao?: string;
  toDataCriacao?: string;
  userIds?: number[];
  onlyUnreadMessage?: boolean;
  etapas?: string[];
  offset: number;
  resultado?: "GANHO" | "PERDIDO" | undefined;
}
const initialFiltersValues = {
  offset: 0,
};

export function AttendancesList() {
  const [filters, setFilters] =
    useState<AttendanceChatFilterProps>(initialFiltersValues);
  const [disabledFilter, setDisabledFilter] = useState<
    "" | "filtro-rapido" | "filtro"
  >("");
  const [usersList, setUsersList] = useState<
    { name: string; value: number; isVendeAI: boolean }[]
  >([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [copyfilters, setCopyFilters] =
    useState<AttendanceChatFilterProps>(initialFiltersValues);
  const [isVendeAiActive, setIsVendeAiActive] = useState(false);
  const { formControl, dispatch } = useAttendanceContext();
  const { user } = useApplicationContext();
  const {
    sendWsMessage,
    connectStatus,
    selectedAttendance,
    setSelectedAttendance,
    attendancesMap,
    setAttendance,
  } = useChatsContext();

  const attendancesList = Array.from(attendancesMap.entries()).map(
    ([k, v]) => v
  );
  const filteredAttendances = sortArrayBy(
    attendancesList,
    "dataAtualizacao",
    "DESC"
  ).filter((currAtendimento: AttendanceChatProps) => {
    return checkIsValidAttendance(filters, currAtendimento);
  });

  const isLoading = connectStatus !== "CONNECTED";

  const loadMoreAttendances = (filters: AttendanceChatFilterProps) => {
    sendWsMessage({ filters, action: "filter-chats" });
  };

  createWebSocketEventListener("atendimento", (data: AttendanceChatProps) => {
    setAttendance(data);
  });

  useEffect(() => {
    if (connectStatus === "CONNECTED") {
      sendWsMessage({ filters, action: "filter-chats" });
    }
  }, [filters, connectStatus]);

  useEffect(() => {
    if (
      formControl.values.id &&
      selectedAttendance &&
      selectedAttendance?.etapa !== formControl.values.etapa
    ) {
      setSelectedAttendance((prev) => ({
        ...prev!,
        etapa: formControl.values.etapa,
      }));
    }
  }, [formControl.values.id]);

  const getVendeAIState = async () => {
    try {
      const { data } = await apiCarteira.get(
        `/services?customer_id=${user.userData.customerId}`
      );
      setIsVendeAiActive(data?.vendeAI?.ativo || false);
    } catch (e) {}
  };

  useEffect(() => {
    getUsers({ setLoading: setIsLoadingUsers, setUsers: setUsersList });
    getVendeAIState();
  }, []);

  const optionsFiltroRapido = [
    // { name: "Todos os atendimentos", value: false },
    { name: "Meus Atendimentos", value: user.userData.userId },
    { name: "(IA) Aguardando Humano", value: "IA_AGUARDANDO_HUMANO" },
    {
      name: "(IA) Todos atendimentos",
      value: usersList
        .filter(({ isVendeAI }) => isVendeAI)
        .map(({ value }) => value),
    },
  ];

  const filtroRapidoValue = optionsFiltroRapido
    .filter(({ value }) =>
      [
        ...(filters.tabulacoes || ([] as any)),
        ...(filters.userIds || ([] as any)),
      ].find((v) => (Array.isArray(value) ? value.includes(v) : value === v))
    )
    .map(({ name }) => name);

  const filtroRapidoDesc =
    disabledFilter === "filtro-rapido" ? undefined : (
      <>
        {filtroRapidoValue.join(" ,")}
        {filtroRapidoValue.length === 1 ? (
          <IconButtonWithLoading
            aria-label=""
            tooltipMessage="Limpar Filtro"
            onClick={async (e) => {
              e.stopPropagation();
              e.preventDefault();
              onChangeFiltroRapido({ offset: 0 }, { disableFiltro: false });
            }}
            icon={<DeleteIcon w="12px" h="12px" />}
            size="xs"
            pos="absolute"
            top="-6px"
            right="0px"
            transform="translateX(calc(20% + 3px))"
            w="18px"
            h="18px"
            minW="0px"
            variant="danger"
          />
        ) : null}
      </>
    );

  const onChangeFiltroRapido = (
    value: AttendanceChatFilterProps,
    { disableFiltro }: { disableFiltro: boolean }
  ) => {
    setFilters(value);
    // if(filtroRapidoValue.length > 1)
    if (disableFiltro) setDisabledFilter("filtro");
    else setDisabledFilter("");
  };

  return (
    <Box
      py="10px"
      bg="#fff"
      overflow="auto"
      maxH={calcTelaChatsHeight()}
      h={calcTelaChatsHeight()}
      onScroll={(e) => {
        const columnElement = e.currentTarget;
        // Altura do elemento
        const elementHeight = columnElement.clientHeight;
        // Distancia percorrida pelo scroll
        const distance = columnElement.scrollTop;
        // Tamanho total do elemento
        const totalElementHeight = columnElement.scrollHeight;
        if (elementHeight + distance >= totalElementHeight) {
          loadMoreAttendances({
            ...filters,
            offset: filteredAttendances.length,
          });
        }
      }}
    >
      <Grid
        bg="#fff"
        pos="sticky"
        top="-10px"
        zIndex={1}
        w="100%"
        templateColumns="auto 110px"
        mt="-10px"
        py="10px"
        px="10px"
        borderBottom="1px solid #E8EAED"
      >
        {user.userData.type === "PARCEIRO_MASTER" && isVendeAiActive ? (
          <Flex pos="relative">
            <DropdownField
              onChange={(value) => {
                if (value === "IA_AGUARDANDO_HUMANO") {
                  value = [value];
                  onChangeFiltroRapido(
                    { offset: 0, tabulacoes: value as TabulacaoType },
                    { disableFiltro: true }
                  );
                } else {
                  if (value) {
                    if (Array.isArray(value)) {
                      onChangeFiltroRapido(
                        { offset: 0, userIds: value as number[] },
                        { disableFiltro: true }
                      );
                    } else {
                      onChangeFiltroRapido(
                        { offset: 0, userIds: [value] as number[] },
                        { disableFiltro: true }
                      );
                    }
                  } else {
                    onChangeFiltroRapido(
                      { offset: 0 },
                      { disableFiltro: false }
                    );
                  }
                }
              }}
              dropdownProps={{
                children:
                  filtroRapidoValue.length === 1 ? filtroRapidoDesc : undefined,
                px: "10px",
                border: "1px solid var(--chakra-colors-gray-200)",
                pos: "relative",
                mr: "8px",
                w: "auto",
                borderRadius: "6px",
                placeholder: "Filtro rápido",
                variant: "ghost",
                alignItems: "center",
                cursor: "pointer",
                _hover: { bg: "gray.200" },
                // leftIcon: <CleanIcon />,
                fontSize: chatsFilterButtonsFontSize,
                fontWeight: "medium",
                menuListProps: { minWidth: "220px" },
                isDisabled:
                  disabledFilter === "filtro-rapido" || isLoadingUsers,
              }}
              options={optionsFiltroRapido}
            />
          </Flex>
        ) : (
          <Box />
          // <Button
          //   px="10px"
          //   mr="8px"
          //   borderRadius="6px"
          //   variant="ghost"
          //   alignItems="center"
          //   cursor="pointer"
          //   _hover={{ bg: "gray.200" }}
          //   leftIcon={<CleanIcon />}
          //   fontSize={chatsFilterButtonsFontSize}
          //   fontWeight="medium"
          //   onClick={() => {
          //     setFilters(initialFiltersValues);
          //     setCopyFilters(initialFiltersValues);
          //   }}
          //   isDisabled={!(Object.keys(filters).length > 1)}
          // >
          //   Limpar Filtros
          // </Button>
        )}

        <AttendanceFilterChats
          filters={filters}
          setFilter={setFilters}
          copyFilters={copyfilters}
          setCopyFilters={setCopyFilters}
          usersList={usersList}
          setUsersList={setUsersList}
          isLoadingUsers={isLoadingUsers}
          disabledFilter={disabledFilter}
          setDisabledFilter={setDisabledFilter}
        />
      </Grid>
      <Loading isLoading={isLoading} />
      {filteredAttendances.map((data: AttendanceChatProps, i) => {
        return (
          <ConversationAttendanceCard
            key={data.id}
            setAttendance={setAttendance}
            dispatch={dispatch}
            formControl={formControl}
            selectedAttendance={selectedAttendance}
            setSelectedAttendance={setSelectedAttendance}
            {...data}
          />
        );
      })}
    </Box>
  );
}

function Loading({ isLoading }: { isLoading: boolean }) {
  const loadingArray = Array.from({ length: 10 });
  return isLoading ? (
    <>
      {loadingArray.map((_, i) => {
        return (
          <SkeletonLoading isLoading={true} key={i}>
            <Box zIndex={i} w="100%" h="69px"></Box>
          </SkeletonLoading>
        );
      })}
    </>
  ) : null;
}
